import React, { useState, useEffect, useCallback } from 'react';
import {
    SimpleForm,
    TextInput,
    required,
    useEditController,
    useDataProvider,
    EditContextProvider,
    maxLength,
    useUnique,
    useNotify,
    useRefresh,
    useTranslate,
} from 'react-admin';
import { Grid, Box, Typography, CircularProgress } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import CustomCloseButton from '@/components/global/buttons/CustomCloseButton.jsx';
import { noCharactersFromList } from '@/components/global/validators/customValidators.jsx';
import SelectWithIcon from '@/components/global/inputs/SelectWithIcon';
import TagCategoryListManager from '@/components/tagCategory/TagCategoryListManager';
import TagCategoryToolbar from '@/components/global/tags/TagCategoryToolbar';
import { colorChoices } from '@/constants/TagCategoryColorChoices';

const TagCategoryEdit = ({ resource = 'tag-category', elementId }) => {
    const translate = useTranslate();
    const controllerProps = useEditController({ resource, id: elementId });
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const location = useLocation();
    const navigate = useNavigate();
    const [category, setCategory] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const unique = useUnique({ filter: { id_neq: elementId } });
    const isFormReady = Boolean(category) && !isLoading && !error;

    const closeForm = useCallback(() => {
        navigate({ pathname: location.pathname, search: location.search }, { replace: true });
    }, [navigate, location.pathname, location.search]);

    useEffect(() => {
        if (elementId) {
            setIsLoading(true);
            dataProvider
                .getOne('tag-category', { id: elementId })
                .then(({ data }) => {
                    setCategory(data);
                    setError(null);
                })
                .catch(err => {
                    setError(err);
                })
                .finally(() => setIsLoading(false));
        }
    }, [dataProvider, elementId]);

    const handleSubmit = data => {
        const updatedData = {
            ...data,
            version: category.version,
        };

        dataProvider
            .update('tag-category', { id: elementId, data: updatedData })
            .then(() => {
                notify(translate('resources.tagCategory.labels.categoryUpdatedSuccessfully'));
                closeForm();
                refresh();
            })
            .catch(err => {
                notify(`${translate('resources.tagCategory.labels.errorUpdatingCategory')}: ${err.message}`, { type: 'error' });
            });
    };

    return (
        <Grid>
            <Box sx={{ width: '100%', padding: '0px' }}>
                <Box className='popup-wrapper popup-wrapper-top'>
                    <Box className='popup-box popup-box--header'>
                        <Typography variant='h4'>{translate('resources.tagCategory.labels.editCategory')}</Typography>
                        <Box>
                            <CustomCloseButton onClose={closeForm} />
                        </Box>
                    </Box>
                </Box>
                {!isFormReady ? (
                    <Box className='popup-wrapper' sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
                        {error ? (
                            <Typography color='error'>
                                {translate('common.errors.loadingData')}: {error.message}
                            </Typography>
                        ) : (
                            <CircularProgress />
                        )}
                    </Box>
                ) : (
                    <Box className='popup-wrapper category-form-wrapper'>
                        <EditContextProvider value={controllerProps} mutationMode='pessimistic' className='category-form'>
                            <SimpleForm
                                initialvalues={category}
                                onSubmit={handleSubmit}
                                toolbar={
                                    <TagCategoryToolbar onCancel={closeForm} cancelLabel={translate('common.cancel')} saveLabel={translate('common.save')} />
                                }
                            >
                                <Box display='flex' gap='20px' width='100%'>
                                    <TextInput
                                        source='name'
                                        label={translate('forms.labels.tagCategoryNameLabel')}
                                        className='category-name'
                                        validate={[
                                            required(translate('common.validation.required')),
                                            maxLength(50, translate('common.validation.maxLength', { count: 50 })),
                                            noCharactersFromList('!@#$^*+\\”"\'`|<>,[]{}'),
                                            unique(),
                                        ]}
                                        fullWidth={true}
                                    />
                                    <TextInput
                                        source='nameEn'
                                        label={translate('forms.labels.tagCategoryNameEnLabel')}
                                        className='category-name'
                                        validate={[
                                            required(translate('common.validation.required')),
                                            maxLength(50, translate('common.validation.maxLength', { count: 50 })),
                                            noCharactersFromList('!@#$^*+\\”"\'`|<>,[]{}'),
                                            unique(),
                                        ]}
                                        fullWidth={true}
                                    />
                                </Box>
                                <SelectWithIcon source='color' className='select-with-icon' colorChoices={colorChoices} />
                                <TagCategoryListManager source='tags' />
                            </SimpleForm>
                        </EditContextProvider>
                    </Box>
                )}
            </Box>
        </Grid>
    );
};

export default TagCategoryEdit;
